import React, { useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  Paper,
  InputAdornment,
  Box,
  Tooltip,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import GrainIcon from "@mui/icons-material/Grain";
import EggAltIcon from "@mui/icons-material/EggAlt";
import OilBarrelIcon from "@mui/icons-material/OilBarrel";

const NutritionForm = () => {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const formRef = useRef(null);

  return (
    <Paper
      elevation={3}
      className="nutrition-form-container"
      ref={formRef}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          py: 1.5,
          px: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "white",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Nutrition Information{" "}
          <Typography variant="body2" component="span" sx={{ opacity: 0.8 }}>
            (per 100g)
          </Typography>
        </Typography>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{
          padding: theme.spacing(3),
          "@media screen and (max-width: 767px)": {
            pt: theme.spacing(2),
            px: theme.spacing(2),
            pb: theme.spacing(6),
          },
        }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="nutrition.calories"
            control={control}
            defaultValue="0"
            render={({ field }) => (
              <Tooltip
                title="Enter total calories in kcal"
                arrow
                placement="top"
              >
                <TextField
                  {...field}
                  label="Calories"
                  variant="outlined"
                  fullWidth
                  type="number"
                  sx={{
                    WebkitTapHighlightColor: "rgba(0,0,0,0.1)",
                    mb: 2,
                    "& .MuiInputBase-root": {
                      "@media (max-width: 767px)": {
                        minHeight: "56px",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: theme.palette.primary.light,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    "& label.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                  error={!!errors?.nutrition?.calories}
                  helperText={errors?.nutrition?.calories?.message || ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalFireDepartmentIcon color="error" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="nutrition.carbohydrate"
            control={control}
            defaultValue="0"
            render={({ field }) => (
              <Tooltip
                title="Enter carbohydrates in grams"
                arrow
                placement="top"
              >
                <TextField
                  {...field}
                  label="Carbohydrates (g)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  sx={{
                    WebkitTapHighlightColor: "rgba(0,0,0,0.1)",
                    mb: 2,
                    "& .MuiInputBase-root": {
                      "@media (max-width: 767px)": {
                        minHeight: "56px",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: theme.palette.primary.light,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    "& label.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                  error={!!errors?.nutrition?.carbohydrate}
                  helperText={errors?.nutrition?.carbohydrate?.message || ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GrainIcon color="warning" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="nutrition.protein"
            control={control}
            defaultValue="0"
            render={({ field }) => (
              <Tooltip title="Enter protein in grams" arrow placement="top">
                <TextField
                  {...field}
                  label="Protein (g)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  sx={{
                    WebkitTapHighlightColor: "rgba(0,0,0,0.1)",
                    mb: 2,
                    "& .MuiInputBase-root": {
                      "@media (max-width: 767px)": {
                        minHeight: "56px",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: theme.palette.primary.light,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    "& label.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                  error={!!errors?.nutrition?.protein}
                  helperText={errors?.nutrition?.protein?.message || ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EggAltIcon color="info" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="nutrition.fats"
            control={control}
            defaultValue="0"
            render={({ field }) => (
              <Tooltip title="Enter fats in grams" arrow placement="top">
                <TextField
                  {...field}
                  label="Fats (g)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  sx={{
                    WebkitTapHighlightColor: "rgba(0,0,0,0.1)",
                    mb: 2,
                    "& .MuiInputBase-root": {
                      "@media (max-width: 767px)": {
                        minHeight: "56px",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: theme.palette.primary.light,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.secondary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    "& label.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                  error={!!errors?.nutrition?.fats}
                  helperText={errors?.nutrition?.fats?.message || ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <OilBarrelIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NutritionForm;
